"use client";

// KEEPS THE ADDRESS BAR, THE COOKIE AND THE LENS ON SCREEN SAYING ONE THING.
//
// The lens is `?as=<key>`, and PersonaProvider derives it from the URL on every
// render. This component makes the URL and the cookie match it:
//
//   STAMPING        a lens holder's entry with no `?as=` — a server page's own
//                   href, a typed address, a bookmark — gets the key of the lens
//                   it was DRAWN through, by a replace, so no entry is added.
//                   Every history entry of an admin, AM, AM Ops or Creative
//                   Specialist therefore names its lens, and Back lands on one
//                   that says what to draw however far it jumps. A `?as=` naming nothing the roster offers is replaced
//                   the same way, with the lens actually shown.
//   THE COOKIE      follows the lens on screen, because it is what a link
//                   WITHOUT `?as=` is rendered through. So Back to an Admin
//                   entry, and then a sidebar link, opens in Admin — not in
//                   whatever lens was switched to since.
//
//   ANYONE WHO      — a creator, a writer, a brand user — has any `?as=`
//   HOLDS NO LENS     removed from the address. The server already ignored it;
//                   a pasted link must not go on saying somebody else's name
//                   in their address bar. (canSwitch decides, never isAdmin: an
//                   AM's own address is `?as=<her slug>`, which the server
//                   stamps on her document loads, and stripping it here would
//                   have it flicker back and forth.)
//   A PAGE SHOWN    — from the disk cache or the back/forward cache — is handled
//   AGAIN           by lib/stale-lens.ts' `onLensShown`: the cookie follows a
//                   page whose URL names its lens, and a page with no `?as=`
//                   (drawn before this shipped) is reloaded if the cookie moved
//                   on since.
//
// ⚠️ AN ENTRY WITH NO `?as=` IS STAMPED WITH THE COOKIE'S LENS, NOT THE ONE
// THE PROVIDER WAS HOLDING. The server drew that page from the cookie, so the
// cookie's lens is the one its data is in; the provider's fallback is the last
// lens a URL named, and the two differ only when something else moved the
// cookie — another tab, say. Stamping the fallback would label the
// server's data with the wrong lens and then write that lens into the cookie.
//
// ⚠️ NOTHING HERE RUNS ON /signin OR /login. They are outside the proxy's
// matcher (proxy.ts), so a `?as=` there reaches nothing, and on the dev bypass —
// which keeps the session, and so this component, alive through sign-out — the
// signed-out lens was stamped onto /signin and written back into the cookie
// sign-out had just deleted (persona-gating.spec.ts, "signing out clears the
// stored view").
//
// A DOCUMENT load never needs a stamp: the root layout sends a lens holder's load
// that names no lens on to the address that does, before the first byte
// (lib/lens-url.ts, lensDocumentRedirect). What is stamped here is a client
// navigation to a link with no `?as=`.
//
// ⚠️ EVERY CORRECTION TO THE ADDRESS IS `router.replace`, NEVER
// `history.replaceState`, though the second would save a server round trip and
// Next does integrate it with its router. Next 16 files each history entry with
// the query its page was RENDERED for (`renderedSearch`), and replaceState
// changes the address without changing that. Back to such an entry then asks
// for the page under one query and gets it under another, and the traversal
// never commits: the address said `?as=admin` while the page went on showing
// the AM, for good (measured on the e2e stack, 2026-09-22 — persona-gating.spec's
// Back-and-Forward test). A replace navigation renders the page under the
// address it writes, so the entry and its render agree. The cost is one
// render of a page for a landing with no `?as=` — which is why the sidebar's
// links carry the lens already (app-sidebar.tsx), and why a page's own links
// should too.

import { useEffect, useLayoutEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAppRouter } from "@/lib/app-router";
import { usePersona } from "./persona-context";
import { LENS_PARAM, hereOf, withLens } from "@/lib/lens-url";
import { cookieLensKey, onLensShown, personaCookieValue, writeLensCookie } from "@/lib/stale-lens";

/**
 * How many "this address is not a page" screens are mounted. See NoLensStamp.
 * Module state, because there is one document and one LensUrlSync per document.
 */
let stampHeld = 0;

/** Pages outside the proxy's matcher: a `?as=` there reaches nothing. */
const LENSLESS = /^\/(signin|login)(\/|$)/;

/**
 * ⚠️ RENDERED BY app/not-found.tsx: A 404 IS NEVER STAMPED. A stamp is a
 * navigation, and a navigation to an address that is not a page is answered as
 * one — on the built image that was a second, full document load racing the
 * next page a test (or a person) asked for, and on the client router a second
 * render whose route announcement put "This page doesn't exist." in the DOM
 * twice (CI, e2e admin 1/3 and 2/3, 2026-09-23). An address that names no page
 * has no lens to name either: Back to it renders the same 404 whatever the
 * cookie says. Mounted as a child, so its layout effect runs before
 * LensUrlSync's effects in the same commit.
 */
export function NoLensStamp() {
  useLayoutEffect(() => {
    stampHeld++;
    return () => {
      stampHeld--;
    };
  }, []);
  return null;
}

export function LensUrlSync({ rendered }: { rendered: string | null }) {
  const { canSwitch, lensKey, switchablePersonas, urlLens } = usePersona();
  const params = useSearchParams();
  // ⚠️ THE PROVIDER'S `?as=`, NOT THE ADDRESS BAR'S. For the one render a
  // same-page Back lands late (components/persona-context.tsx, PR #413) the
  // lens on screen is the previous entry's while the address is already the
  // restored one; read raw, that pair is "the address names another lens",
  // and the stamp below would replace the entry just gone back to with the one
  // just left. The provider's pair always agrees with itself.
  const as = urlLens;
  // And while it lags the address at all, nothing is decided: the render that
  // catches up follows at once and runs both effects again with `as` moved.
  const behind = as !== (params?.get(LENS_PARAM) ?? null);
  // ⚠️ THE WHOLE ADDRESS, not only its lens, decides when to look again. A
  // stamp is a navigation, and a click that lands before it commits supersedes
  // it — so the next address also has no `?as=`, `as` is null both times, and an
  // effect keyed on `as` alone never ran again (brand-record.spec, a tab then a
  // window pick in quick succession, 2026-09-22).
  const address = `${usePathname()}?${params?.toString() ?? ""}`;
  const router = useAppRouter();

  // The pageshow listener outlives renders, so it reads the latest of these.
  const latest = useRef({ rendered, lensKey, switchablePersonas });
  useLayoutEffect(() => {
    latest.current = { rendered, lensKey, switchablePersonas };
  }, [rendered, lensKey, switchablePersonas]);

  // Set when a reload has been started, so nothing below touches the address or
  // the cookie of a document that is on its way out.
  const reloading = useRef(false);

  // ⚠️ ONCE AT MOUNT, AND ON A THAWED PAGE — NEVER ON A RE-RENDER. A mount is a
  // document hydrating, which is when a cached one arrives; a thaw mounts nothing
  // and runs no effect, hence the listener. Declared BEFORE the effect below, so
  // at mount React runs it first and a reload is decided before anything stamps.
  useEffect(() => {
    if (!canSwitch) return;
    const shown = () =>
      onLensShown({
        search: window.location.search,
        current: latest.current.lensKey,
        switchable: latest.current.switchablePersonas,
        rendered: latest.current.rendered,
        cookieHeader: document.cookie,
        location: window.location,
        writeCookie: writeLensCookie,
      });
    if (shown() === "reloaded") reloading.current = true;
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted && shown() === "reloaded") reloading.current = true;
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, [canSwitch]);

  // EVERY TIME THE URL'S LENS OR THE LENS ON SCREEN CHANGES: a switch landing,
  // Back, Forward, a link with no `?as=`.
  useEffect(() => {
    if (behind || reloading.current || LENSLESS.test(window.location.pathname)) return;
    const stamp = (key: string | null) => {
      if (stampHeld > 0) return; // a 404 — see NoLensStamp
      router.replace(withLens(hereOf(window.location), key), { scroll: false });
    };
    if (!canSwitch) {
      if (as !== null) stamp(null);
      return;
    }
    if (as === null) {
      // Drawn from the cookie, so named by it. The provider picks the key up
      // off the address on the next render, and this runs again with it.
      stamp(cookieLensKey(document.cookie, switchablePersonas));
      return;
    }
    if (lensKey && as !== lensKey) stamp(lensKey);
  }, [behind, canSwitch, as, address, lensKey, switchablePersonas, router]);

  // THE COOKIE FOLLOWS THE LENS ON SCREEN — when the LENS changes, and only
  // then. Not on every address: a second tab sits on its own lens, and a page
  // in it that moved its own params (an editor, a pick) would otherwise write
  // that tab's lens over the one the other tab had just switched to
  // (persona-back.spec, which moves the cookie from a second tab).
  //
  // THE COOKIE AS WRITTEN, not the lens it resolves to: an absent cookie, or one
  // still holding the JSON from before the key, is written as the key.
  useEffect(() => {
    if (behind || reloading.current || LENSLESS.test(window.location.pathname)) return;
    if (!canSwitch || !lensKey || as !== lensKey) return;
    if (personaCookieValue(document.cookie) !== lensKey) writeLensCookie(lensKey);
  }, [behind, canSwitch, as, lensKey]);

  return null;
}
